import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { GoogleMapsModule } from '@angular/google-maps';

import { NgbModule, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';

import { AddressFormComponent } from './components/address-form/address-form.component';
import { FooterCardsComponent } from './components/footer-cards/footer-cards.component';
import { IconsModule } from './modules/icons/icons.module';
import { MatchingFieldsDirective } from './directives/matching-fields.directive';
import { PerkWidgetComponent } from './components/perk-widget/perk-widget.component';
import { PostcodeFieldComponent } from './components/postcode-field/postcode-field.component';
import { PremiumBenefitsComponent } from './components/premium-benefits/premium-benefits.component';
import { PricingTableComponent } from './components/pricing-table/pricing-table.component';
import { SwitcherComponent } from './components/switcher/switcher.component';
import { CustomNgbDateParserFormatter } from '../services/date-picker/custom-ngb-date-parser-formatter.service';
import {
  IsDateBeforeDirective,
  IsDateAfterDirective,
} from './directives/is-date-before-after.directive';
import { ImagePreviewDirective } from './directives/image-preview.directive';
import { CloudinaryModule } from '@cloudinary/ng';
import { EmailDotValidatorDirective } from './directives/email-dot-validator.directive';
import { TrackClickDirective } from './directives/track-click.directive';
import { PremiumBenefitsPitPatComponent } from './components/premium-benefits-pit-pat/premium-benefits-pit-pat.component';
import { ReadMoreComponent } from './components/read-more/read-more.component';
import { PetRehomeModalHeaderComponent } from './components/pet-rehome-modal-header/pet-rehome-modal-header.component';
import { TrustboxComponent } from './components/trustbox/trustbox.component';
import { TrustboxTestimonialsDesktopComponent } from './components/trustbox-testimonials-desktop/trustbox-testimonials-desktop.component';
import { TrustboxTestimonialsMobileComponent } from './components/trustbox-testimonials-mobile/trustbox-testimonials-mobile.component';
import { TrustboxFooterWidgetComponent } from './components/trustbox-footer-widget/trustbox-footer-widget.component';
import { AndroidAppBanner } from './components/android-app-banner/android-app-banner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { SocialBandComponent } from './components/social-band/social-band.component';
import { SocialFacebookComponent } from './components/social-band/social-facebook/social-facebook.component';
import { SocialInstagramComponent } from './components/social-band/social-instagram/social-instagram.component';
import { SocialTwitterComponent } from './components/social-band/social-twitter/social-twitter.component';
import { ChargeReasonModalComponent } from '../pages/charge-reason-modal/charge-reason-modal.component';
import { UpdatePetDetailsInfoModalComponent } from '../pages/update-pet-details-info-modal/update-pet-details-info-modal.component';
import { IdentialertSubSelectionModalComponent } from './components/subscription-pages/identialert-sub-selection-modal/identialert-sub-selection-modal.component';
import { SubscriptionTableDesktopComponent } from './components/subscription-pages/subscription-tables/desktop/subscription-table-desktop.component';
import { SubscriptionTableMobileComponent } from './components/subscription-pages/subscription-tables/mobile/subscription-table-mobile.component';
import { EmailMustEndWithDirective } from './directives/email-must-end-with-validator.directive';
import { FinishWelcomeGiftStepModalComponent } from '../pages/finish-welcome-gift-step-modal/finish-welcome-gift-step-modal.component';
import { FacebookLoginComponent } from '../pages/facebook-login/facebook-login.component';
import {
  FacebookLoginProvider,
  SocialLoginModule,
  SocialAuthServiceConfig
} from '@abacritt/angularx-social-login';
import { ChipCheckerComponent } from './components/chip-checker/chip-checker.component';
import { HomeHeroComponent } from './components/home-hero/home-hero.component';
import { RegisterStepperComponent } from './components/register-stepper/register-stepper.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { HomeStepsComponent } from './components/home-steps/home-steps.component';
import { StepCardComponent } from './components/home-steps/step-card/step-card.component';
import { PriceCardComponent } from './components/pricing-table/price-card/price-card.component';
import { ProductHeroComponent } from './components/subscription-pages/product-hero/product-hero.component';
import { LocateMicrochipStepComponent } from './components/locate-microchip-step/locate-microchip-step.component';
import { NextStepCardComponent } from './components/next-step-card/next-step-card.component';
import { BrowserModule } from '@angular/platform-browser';
import { HomeCarouselComponent } from './components/home-carousel/home-carousel.component';
import { DigitalIdSlideComponent } from './components/home-carousel/digital-id-slide/digital-id-slide.component';
import { CatChippingSlideComponent } from './components/home-carousel/cat-chipping-slide/cat-chipping-slide.component';
import { NmmSlideComponent } from './components/home-carousel/nmm-slide/nmm-slide.component';
import { DowloadAppSlideComponent } from './components/home-carousel/dowload-app-slide/dowload-app-slide.component';
import { MainPlanCardComponent } from './components/main-plan-card/main-plan-card.component';
import { TransferPetCardComponent } from './components/transfer-pet-card/transfer-pet-card.component';
import { TokHeaderComponent } from './components/tok-header/tok-header.component';
import { MadeItHomeSafelySlideComponent } from './components/home-carousel/made-it-home-safely-slide/made-it-home-safely-slide.component';
import { RecommendedAppSlideComponent } from './components/home-carousel/recommended-app-slide/recommended-app-slide.component';
import { FixedPricingTableComponent } from './components/fixed-pricing-table/fixed-pricing-table.component';

@NgModule({
  declarations: [
    AddressFormComponent,
    EmailDotValidatorDirective,
    EmailMustEndWithDirective,
    FooterCardsComponent,
    IsDateAfterDirective,
    IsDateBeforeDirective,
    ImagePreviewDirective,
    MatchingFieldsDirective,
    TrackClickDirective,
    PerkWidgetComponent,
    PostcodeFieldComponent,
    PremiumBenefitsComponent,
    PremiumBenefitsPitPatComponent,
    PricingTableComponent,
    FixedPricingTableComponent,
    PriceCardComponent,
    ReadMoreComponent,
    SwitcherComponent,
    PetRehomeModalHeaderComponent,
    TrustboxComponent,
    TrustboxFooterWidgetComponent,
    TrustboxTestimonialsDesktopComponent,
    TrustboxTestimonialsMobileComponent,
    AndroidAppBanner,
    SocialBandComponent,
    SocialFacebookComponent,
    SocialInstagramComponent,
    SocialTwitterComponent,
    ChargeReasonModalComponent,
    UpdatePetDetailsInfoModalComponent,
    SubscriptionTableDesktopComponent,
    SubscriptionTableMobileComponent,
    IdentialertSubSelectionModalComponent,
    FinishWelcomeGiftStepModalComponent,
    FacebookLoginComponent,
    ChipCheckerComponent,
    HomeHeroComponent,
    ProductHeroComponent,
    RegisterStepperComponent,
    PasswordStrengthComponent,
    HomeStepsComponent,
    StepCardComponent,
    LocateMicrochipStepComponent,
    NextStepCardComponent,
    HomeCarouselComponent,
    DigitalIdSlideComponent,
    CatChippingSlideComponent,
    NmmSlideComponent,
    DowloadAppSlideComponent,
    MadeItHomeSafelySlideComponent,
    RecommendedAppSlideComponent,
    MainPlanCardComponent,
    TransferPetCardComponent,
    TokHeaderComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    GoogleMapsModule,
    CloudinaryModule,
    FormsModule,
    IconsModule,
    NgbModule,
    RouterModule,
    MatCheckboxModule,
    MatChipsModule,
    MatProgressBarModule,
    SocialLoginModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
  ],
  exports: [
    AddressFormComponent,
    GoogleMapsModule,
    CommonModule,
    CloudinaryModule,
    EmailDotValidatorDirective,
    EmailMustEndWithDirective,
    FooterCardsComponent,
    FormsModule,
    IconsModule,
    IsDateAfterDirective,
    IsDateBeforeDirective,
    ImagePreviewDirective,
    MatchingFieldsDirective,
    TrackClickDirective,
    NgbModule,
    PerkWidgetComponent,
    PostcodeFieldComponent,
    PremiumBenefitsComponent,
    PremiumBenefitsPitPatComponent,
    PricingTableComponent,
    FixedPricingTableComponent,
    PriceCardComponent,
    ReadMoreComponent,
    RouterModule,
    SwitcherComponent,
    PetRehomeModalHeaderComponent,
    TrustboxComponent,
    TrustboxFooterWidgetComponent,
    TrustboxTestimonialsDesktopComponent,
    TrustboxTestimonialsMobileComponent,
    AndroidAppBanner,
    MatCheckboxModule,
    MatChipsModule,
    MatProgressBarModule,
    MatSlideToggleModule,
    MatRadioModule,
    SocialBandComponent,
    SocialFacebookComponent,
    SocialInstagramComponent,
    SocialTwitterComponent,
    ChargeReasonModalComponent,
    UpdatePetDetailsInfoModalComponent,
    SubscriptionTableDesktopComponent,
    SubscriptionTableMobileComponent,
    IdentialertSubSelectionModalComponent,
    FinishWelcomeGiftStepModalComponent,
    FacebookLoginComponent,
    ChipCheckerComponent,
    HomeHeroComponent,
    ProductHeroComponent,
    RegisterStepperComponent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    PasswordStrengthComponent,
    HomeStepsComponent,
    StepCardComponent,
    LocateMicrochipStepComponent,
    NextStepCardComponent,
    HomeCarouselComponent,
    MainPlanCardComponent,
    TransferPetCardComponent,
    TokHeaderComponent,
  ],
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateParserFormatter },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider('1872749693061425'),
          },
        ],
      } as SocialAuthServiceConfig,
    },
  ],
})
export class SharedModule {}
